/**
 * Root route: the top of the router tree. Renders whatever the router
 * matched and catches anything that throws above the authenticated layout
 * (route configuration errors, unexpected render failures). Auth itself is
 * handled outside the router, by {@link EntraSessionGate} — see
 * src/router/routes.tsx for why.
 *
 * @module router/RootLayout
 */

import { Outlet, useRouteError } from "react-router";

import { Alert, ConfirmProvider, Heading, LinkButton, Panel, ToastProvider } from "@/components/ui";

/**
 * The confirmation and toast providers live here rather than in the
 * authenticated shell, so the not-found route outside that shell can still
 * raise one.
 *
 * Not the error route: `RootErrorBoundary` is this same route's `ErrorBoundary`
 * and renders instead of this component, so neither provider is mounted for it.
 * Anything added there that calls `useToast` or `useConfirm` throws inside the
 * crash screen.
 */
export function RootLayout() {
  return (
    <ConfirmProvider>
      <ToastProvider>
        <Outlet />
      </ToastProvider>
    </ConfirmProvider>
  );
}

export function RootErrorBoundary() {
  const error = useRouteError();
  const message = error instanceof Error ? error.message : "An unexpected error occurred.";
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-base-100 px-4">
      <Panel className="w-full max-w-lg">
        <Heading level={1} size="lg">
          This page failed to load
        </Heading>
        <div className="mt-3">
          <Alert tone="warning">{message}</Alert>
        </div>
        {/* A document load, not a route change: the tree that just threw is
            the tree a client-side navigation would re-render. */}
        <LinkButton to="/" reloadDocument variant="primary" icon="house" className="mt-5">
          Back to overview
        </LinkButton>
      </Panel>
    </div>
  );
}
